import {
  default as EChartsReact,
  default as ReactEcharts,
} from "echarts-for-react";
import { useEffect, useState } from "react";

import { t } from "i18next";
import { DateTime } from "luxon";
import { DateRange } from "react-day-picker";
import { hexColor, hslColor, rgbColor } from "../../enums/colors";
import { convertTimezone } from "../../lib/utils";
import { useAppStore } from "../../stores/RootStore";
import { color } from "echarts";
import _ from "lodash";
import Spinner from "../Spinner/Spinner";

interface BaseChartProps {
  type: string;
  title: string;
  data: ChartData[];
  selectedDate: DateRange;
  onChangeDates: Function;
  loading: boolean;
  idleLevel?: number;
  withStat?: boolean;
  trigger: { max: number | null | undefined; min: number | null | undefined };
}

interface ChartData {
  at: number;
  v: number;
}

const BarMilkChart = ({
  data,
  selectedDate,
  title,
  onChangeDates,
  loading,
  idleLevel,
  withStat = false,
  trigger,
}: BaseChartProps) => {
  const store = useAppStore();
  const [ref, setRef] = useState<EChartsReact | null>();
  const [loaded, setLoaded] = useState(false);
  const [defaultPeriod, setDefaultPeriod] = useState(true);
  const [currentIntervalType, setCurrentIntervalType] =
    useState<string>("minutes");
  const [minmax, setMinmax] = useState({ min: 0, max: 0 });
  const [zoomed, setZoomed] = useState(false);
  const [reset, setReset] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [cycleStatsData, setCycleStatsData] = useState<any[]>([]);
  const [points, setPoints] = useState({ start: 0, end: 100 });
  const [seconds, setSeconds] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const [sumValues, setSumValues] = useState(0);
  const [totalCycles, setTotalCycles] = useState(0);
  const [violations, setViolations] = useState(0);
  const echartInstance = ref?.getEchartsInstance();

  useEffect(() => {
    if (selectedDate.to) {
      setLoaded(true);
      setZoomed(false);
      setPoints({ start: 0, end: 100 });
    }
  }, [selectedDate]);

  useEffect(() => {
    if (loading) {
      setLoaded(false);
    }
  }, [loading]);

  useEffect(() => {
    if (!data) return;
    const CYCLE_THRESHOLD = 0
    
    let itemData: any[] = [];
    let cyclesData: any[] = [];
    let sum = 0;
    let moreThan: number[] = [];
    let lessThan: number[] = [];
    let previousValue = 0
    let cycles = 0
    let cycleValue = 0;

    setMinmax({ min: data[0].at, max: data[data.length - 1].at });
    setSeconds(data.length > 2 ? (data[2].at - data[1].at) / 1000 : 0);

    data.forEach((it) => {
      itemData.push([
        DateTime.fromMillis(it.at).toUTC().toJSDate(),
        it.v ? (it.v > 0 ? it.v / 1000000 : 0) : null,
      ]);
      // Начало цикла
      if (it.v > CYCLE_THRESHOLD && previousValue <= CYCLE_THRESHOLD) {
        cycles = cycles + 1;
        cycleValue = cycleValue + it.v;
      }
      // Конец цикла
      if (it.v <= CYCLE_THRESHOLD && previousValue > CYCLE_THRESHOLD) {
        cyclesData.push({
          x: cycles,
          y: cycleValue / 1000000,
        });
        cycleValue = 0;
      }
      // Середина цикла
      if (it.v && it.v > 0 && previousValue > CYCLE_THRESHOLD) {
        cycleValue = cycleValue + it.v;
      }

      sum += it.v ? it.v : 0;
      if (trigger.max && it.v > trigger.max) {
        moreThan.push(it.v);
      }
      if (trigger.min && it.v < trigger.min) {
        lessThan.push(it.v);
      }

      previousValue = it.v;
    });

    // Закончить цикл, если данные заканчиваются в активном цикле
    if (cycleValue > 0) {
      cyclesData.push({
        x: cycles,
        y: cycleValue / 1000000,
      });
      cycleValue = 0;
    }

    setMaxValue(_.maxBy(data, "v")?.v! / 1000000);
    setSumValues(sum / 1000000);
    setTotalCycles(cycles);
    setViolations(moreThan.length + lessThan.length);
    setChartData(itemData);
    setCycleStatsData(cyclesData);
    setLoaded(true);
  }, [data]);

  useEffect(() => {
    if (zoomed) {
      onChangeDates({
        from:
          points.start < 101
            ? selectedDate.from!
            : DateTime.fromMillis(points.start).toJSDate(),
        to:
          points.start < 101
            ? selectedDate.to!
            : DateTime.fromMillis(points.end).toJSDate(),
      });
    } else {
      onChangeDates({
        from: selectedDate.from!,
        to: selectedDate.to!,
      });
    }
  }, [zoomed, points]);

  const getOptions = () => ({
    animation: false,
    title: {
      text: title,
      textStyle: {
        fontSize: 12,
        lineHeight: 36,
        fontWeight: "normal",
        color:
          store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
      },
      padding: 0,
      textVerticalAlign: "auto",
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
        label: {
          // show: true,
          color:
            store.theme === "dark"
              ? hexColor.borderForeground
              : hexColor.border,
          formatter: function (params: { value: number }) {
            return DateTime.fromMillis(params.value!).toFormat(
              "HH:mm:ss dd.MM.yyyy",
            );
          },
        },
      },
    },
    grid: {
      left: null,
      right: null,
      bottom: "10px",
      top: "44px",
      height: 160,
      backgroundColor: "transparent",
      borderColor:
        store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
      containLabel: true,
    },
    xAxis: [
      {
        type: "time",
        boundaryGap: false,
        min: minmax.min + 2,
        max: minmax.max - 1,
        splitNumber: 12,
        showMaxLabel: false,
        alignMaxLabel: "right",
        showMinLabel: false,
        alignMinLabel: "left",
        axisLine: {
          lineStyle: {
            color:
              store.theme === "dark"
                ? hexColor.borderForeground
                : hexColor.border,
            width: 0.5,
          },
        },
        axisLabel: {
          color:
            store.theme === "dark"
              ? hexColor.border
              : hexColor.borderForeground,
          formatter: (val: number) =>
            `${DateTime.fromMillis(val).toFormat("HH:mm:ss")}\n${minmax.max - minmax.min > 172800000 ? DateTime.fromMillis(val).toFormat("dd.MM.yy") : ""}`,
          lineHeight: 16,
          overflow: "truncate",
        },
      },
    ],
    yAxis: {
      type: "value",
      offset: 0,
      position: "right",
      splitNumber: 3,
      axisLabel: {
        inside: true,
        color:
          store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
      },
      splitLine: {
        lineStyle: {
          color:
            store.theme === "dark"
              ? hexColor.borderForeground
              : hexColor.border,
          type: "dashed",
          dashOffset: 10,
        },
      },
    },
    toolbox: {
      itemGap: 0,
      itemSize: 12,
      feature: {
        dataZoom: {
          title: {
            zoom: t("zoom"),
            back: zoomed ? t("back") : "",
          },
          icon: {
            zoom: "path://m4 8v-2c0-1.1.9-2 2-2h2m-4 12v2c0 1.1.9 2 2 2h2m-4-4v2c0 1.1.9 2 2 2h2m8-16h2c1.1 0 2 .9 2 2v2m-4 12h2c1.1 0 2-.9 2-2v-2m-12-5c0 1.7 1.3 3 3 3s3-1.3 3-3-1.3-3-3-3-3 1.3-3 3m8 5-2.5-2.5",
            back: zoomed
              ? "path://M3 16m0 1a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1z M4 12v-6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-6 M15 13h-4v-4 M11 13l5 -5"
              : "blank",
          },
          iconStyle: {
            borderColor: zoomed ? rgbColor.primary : rgbColor.foreground,
            borderWidth: 1.5,
          },
          emphasis: {
            iconStyle: {
              color: "none",
              opacity: 0,
              borderColor: rgbColor.primary,
              borderWidth: 1.5,
              textFill: rgbColor.primary,
            },
          },
          yAxisIndex: false,
          filterMode: "empty",
        },
        restore: {
          show: zoomed,
          title: t("reset"),
          iconStyle: {
            borderColor: zoomed ? rgbColor.primary : rgbColor.foreground,
            borderWidth: 1.5,
          },
          emphasis: {
            iconStyle: {
              color: "none",
              borderColor: rgbColor.primary,
              borderWidth: 1.5,
              textFill: rgbColor.primary,
            },
          },
        },
      },
    },
    series: [
      {
        name: title,
        type: "bar",
        data: chartData,
        smooth: true,
        symbol: "none",
        areaStyle: {
          color: rgbColor.primary,
        },
        lineStyle: {
          color: rgbColor.primary,
          width: 0.5,
        },
        markLine: idleLevel
          ? {
              silent: true,
              symbol: ["none", "none"],
              precision: 0,
              lineStyle: {
                color:
                  store.theme === "light"
                    ? hexColor.destructive
                    : hexColor.destructive,
              },
              data: [
                {
                  name: t("idle_level"),
                  label: {
                    color:
                      store.theme === "light"
                        ? hexColor.destructive
                        : hexColor.destructive,
                    show: true,
                    formatter: "{b}",
                    position: "insideStartTop",
                  },
                  yAxis: idleLevel,
                },
              ],
              tooltip: {
                trigger: "none",
              },
            }
          : {},
      },
    ],
  });

  const options = {
    title: {
      text: "Статистика по циклам",
      textStyle: {
        fontSize: 12,
        lineHeight: 36,
        fontWeight: "normal",
        color:
          store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
      },
      padding: 0,
      textVerticalAlign: "auto",
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
    },
    xAxis: [
      {
        type: "category",
        axisTick: {
          alignWithLabel: true,
        },
      },
    ],
    yAxis: [
      {
        type: "value",
      },
    ],
    series: [
      {
        name: `Статистика по циклу (л)`,
        type: "bar",
        barWidth: "60%",
        data: cycleStatsData.map((obj) => [obj.x, obj.y]),
      },
    ],
  };

  echartInstance?.on("datazoom", "mouseup", function (params: any) {
    if (params.batch[0].startValue && params.batch[0].endValue) {
      setPoints({
        start: Math.round(params.batch[0].startValue),
        end: Math.round(params.batch[0].endValue),
      });
      setZoomed(true);
    } else {
      setPoints({ start: params.batch[0].start, end: params.batch[0].end });
      setZoomed(false);
    }
  });

  echartInstance?.on("restore", "click", function (params: any) {
    setZoomed(false);
    setLoaded(false);
    onChangeDates({
      from: selectedDate.from!,
      to: selectedDate.to!,
    });
  });

  echartInstance?.dispatchAction({
    type: "takeGlobalCursor",
    key: "dataZoomSelect",
    // Activate or inactivate.
    dataZoomSelectActive: true,
  });

  return loading ? (
    <div className="h-56 w-full">
      {" "}
      <Spinner />
    </div>
  ) : (
    <div className="flex flex-col">
      <ReactEcharts
        option={getOptions()}
        showLoading={!loaded}
        lazyUpdate={true}
        loadingOption={{
          text: "",
          color: hslColor.primary,
          // textColor: store.theme === "dark" ? hslColor.lightBorder : hslColor.darkBorder,
          maskColor:
            store.theme === "dark"
              ? hexColor.borderForeground
              : hexColor.border,
          zlevel: 0,
          fontSize: 12,
          showSpinner: true,
          spinnerRadius: 8,
          lineWidth: 2,
          fontWeight: "normal",
          fontStyle: "normal",
          fontFamily: "sans-serif",
        }}
        ref={(e) => {
          setRef(e);
        }}
        style={{ width: "100%", height: 208 }}
        opts={{ renderer: "canvas" }}
      />
      {loaded && withStat && (
        <div className="text-xs font-medium h-4 flex items-center space-x-2">
          <span>
            {t("max_value")}: {maxValue}
          </span>
          <span>
            {t("pumped_over_period")}: {sumValues.toFixed(2)}
          </span>
          <span>
            {t("total_cycles")}: {totalCycles}
          </span>
          <span>
            {t("seconds_in_point")}: {seconds}
          </span>
        </div>
      )}
      <ReactEcharts
        option={options}
        showLoading={!loaded}
        lazyUpdate={true}
        loadingOption={{
          text: "",
          color: hslColor.primary,
          maskColor:
            store.theme === "dark"
              ? hexColor.borderForeground
              : hexColor.border,
          zlevel: 0,
          fontSize: 12,
          showSpinner: true,
          spinnerRadius: 8,
          lineWidth: 2,
          fontWeight: "normal",
          fontStyle: "normal",
          fontFamily: "sans-serif",
        }}
        style={{ width: "100%", height: 208 }}
        opts={{ renderer: "canvas" }}
      />
    </div>
  );
};

export default BarMilkChart;
